import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  List,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  BankOutlined,
  GlobalOutlined,
  HistoryOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useTheme } from 'styled-components';
import {
  Link,
  useBeforeUnload,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  isJobChainTemplateApiFailure,
  isJobChainTemplateProblemDetails,
  jobChainTemplates,
  unwrapTemplateResponse,
  useCacheTemplateDetail,
  useInvalidateTemplates,
  useJobChainTemplateDetail,
  useJobChainTemplateMetadata,
  type JobChainTemplateAction,
  type JobChainTemplateDetail,
  type JobChainTemplateGraph,
  type JobChainTemplateIssue,
  type JobChainTemplateMetadata,
  type JobChainTemplateValidation,
} from '@/api/JobChainTemplates';
import {
  ConfirmDialog,
  templateFailureText,
  templateListPath,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/editorShared';
import { GraphAuthor } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/GraphAuthor';
import { PreservedGraphFields } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/PreservedGraphFields';
import { Loader } from '@/ui/components/Loader';
import { faDiagramProject, PageContainer, Title } from '@/ui/components';
import { type TFunc, useTranslate } from '@/translations';
import {
  DetailCard,
  DetailMessages,
  IssueButton,
  IssueList,
  MetadataItem,
  MetadataSection,
  SupportingActions,
  TabSection,
  ValidationSummary,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/TemplateEditor.styled';
import {
  graphUnsupportedReasons,
  resolveIssueTarget,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/graphSupport';
import {
  templateScopeLabel,
  templateStatusLabel,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/templateLabels';

const auditActionText = (action: string, t: TFunc) => {
  const keys: Record<string, Parameters<TFunc>[0]> = {
    Create: 'jobChainTemplateEditorAuditActionCreate',
    Edit: 'jobChainTemplateEditorAuditActionEdit',
    Save: 'jobChainTemplateEditorAuditActionSave',
    SaveGraph: 'jobChainTemplateEditorAuditActionSaveGraph',
    Validate: 'jobChainTemplateEditorAuditActionValidate',
    Publish: 'jobChainTemplateEditorAuditActionPublish',
    Discard: 'jobChainTemplateEditorAuditActionDiscard',
    CreateDraft: 'jobChainTemplateEditorAuditActionCreateDraft',
    Conflict: 'jobChainTemplateEditorAuditActionConflict',
  };
  return keys[action] ? t(keys[action]) : action;
};

const TemplatePage = ({ children }: { children: React.ReactNode }) => {
  const { accent } = useTheme();
  const { t } = useTranslate();
  return (
    <PageContainer>
      <Title
        title={t('jobChainTemplateEditorTitle')}
        icon={faDiagramProject}
        color={accent}
      />
      {children}
    </PageContainer>
  );
};

export const TemplateEditor = ({
  mode = 'view',
}: {
  mode?: 'view' | 'edit';
}) => {
  const { t } = useTranslate();
  const { id } = useParams();
  const templateId = Number(id);
  const detail = useJobChainTemplateDetail(templateId);
  const metadata = useJobChainTemplateMetadata();
  const { search } = useLocation();
  if (!Number.isInteger(templateId) || templateId <= 0)
    return (
      <TemplatePage>
        <DetailCard
          title={t('jobChainTemplateEditorTemplateTitle')}
          extra={
            <Link to={`${templateListPath}${search}`}>
              {t('jobChainTemplatesBackToTemplates')}
            </Link>
          }
          styles={{ body: { padding: 16 } }}
        >
          <Alert
            type="error"
            showIcon
            message={t('jobChainTemplateEditorInvalidId')}
          />
        </DetailCard>
      </TemplatePage>
    );
  if (detail.data && metadata.data)
    return (
      <TemplatePage>
        <TemplateSession
          key={`${templateId}:${mode}`}
          mode={mode}
          initial={detail.data}
          metadata={metadata.data}
          dependencyError={detail.error || metadata.error}
        />
      </TemplatePage>
    );
  if (!detail.isError && !metadata.isError)
    return (
      <div
        role="status"
        aria-label={t('jobChainTemplateEditorLoading')}
        aria-busy="true"
      >
        <Loader />
      </div>
    );
  return (
    <TemplatePage>
      <DetailCard
        title={t('jobChainTemplateEditorTemplateTitle')}
        extra={
          <Link to={`${templateListPath}${search}`}>
            {t('jobChainTemplatesBackToTemplates')}
          </Link>
        }
        styles={{ body: { padding: 16 } }}
      >
        <Alert
          type="error"
          showIcon
          message={templateFailureText(detail.error || metadata.error, t)}
          action={
            <Button
              onClick={() => {
                void detail.refetch();
                void metadata.refetch();
              }}
            >
              {t('jobChainTemplateEditorRetryLoading')}
            </Button>
          }
        />
      </DetailCard>
    </TemplatePage>
  );
};

const TemplateSession = ({
  mode,
  initial,
  metadata,
  dependencyError,
}: {
  mode: 'view' | 'edit';
  initial: JobChainTemplateDetail;
  metadata: JobChainTemplateMetadata;
  dependencyError: unknown;
}) => {
  const { t } = useTranslate();
  const [saved, setSaved] = useState(initial);
  const [graph, setGraph] = useState(initial.graph);
  const [validation, setValidation] =
    useState<JobChainTemplateValidation | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [selection, setSelection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('workflow');
  const [error, setError] = useState('');
  const [issues, setIssues] = useState<JobChainTemplateIssue[]>([]);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [confirmation, setConfirmation] = useState<
    'publish' | 'discard' | 'reload' | null
  >(null);
  const operation = useRef(false);
  const skipGuard = useRef(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const lastServerDetail = useRef(initial);
  const navigate = useNavigate();
  const { search } = useLocation();
  const invalidate = useInvalidateTemplates();
  const cacheDetail = useCacheTemplateDetail();
  const dirty = JSON.stringify(graph) !== JSON.stringify(saved.graph);
  useEffect(() => {
    if (lastServerDetail.current === initial) return;
    lastServerDetail.current = initial;
    // Refresh clean cached detail, but a background query must never replace local edits
    // or a command response while that command is still being reconciled.
    if (dirty || busy || uncertain) return;
    setSaved(initial);
    setGraph(initial.graph);
    if (initial.etag !== saved.etag || initial.status !== saved.status) {
      setValidation(null);
      setAcknowledged(false);
      setIssues([]);
    }
  }, [initial, dirty, busy, uncertain, saved.etag, saved.status]);
  const blocker = useBlocker(
    () => !skipGuard.current && (dirty || operation.current),
  );
  useBeforeUnload((event) => {
    if (dirty || operation.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    if (error || issues.length) summaryRef.current?.focus();
  }, [error, issues]);
  const unsupported = [
    ...saved.unsupportedReasons.map((issue) => issue.message),
    ...graphUnsupportedReasons(saved.graph, metadata, t),
  ];
  const can = (action: JobChainTemplateAction) =>
    !unsupported.length &&
    !dependencyError &&
    initial.availableActions.includes(action) &&
    saved.availableActions.includes(action);
  const existingDraft = saved.history.find(
    (version) => version.status === 'Draft' && version.id !== saved.id,
  );
  const editingDraft = mode === 'edit' && saved.status === 'Draft';
  const editable = editingDraft && can('SaveGraph') && !busy && !uncertain;
  const currentValidation =
    validation?.etag === saved.etag && !dirty && validation.isValid;
  const warnings = validation?.issues.filter((issue) => issue.isWarning) || [];
  const publishReady =
    editingDraft &&
    can('Publish') &&
    currentValidation &&
    (!warnings.length || acknowledged) &&
    !busy &&
    !uncertain;
  const clearValidation = () => {
    setValidation(null);
    setAcknowledged(false);
    setIssues([]);
  };
  const adopt = (detail: JobChainTemplateDetail) => {
    setSaved(detail);
    setGraph(detail.graph);
    clearValidation();
    setError('');
  };
  const fail = (failure: unknown) => {
    setError(templateFailureText(failure, t));
    if (
      isJobChainTemplateApiFailure(failure) &&
      isJobChainTemplateProblemDetails(failure.message) &&
      Array.isArray(failure.message.issues)
    )
      setIssues(failure.message.issues as JobChainTemplateIssue[]);
  };
  const run = async (work: () => Promise<void>) => {
    if (operation.current) return;
    operation.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
    } catch (failure) {
      fail(failure);
    } finally {
      operation.current = false;
      setBusy(false);
    }
  };
  const reconcile = async () => {
    // Both reads must succeed before another publish can be offered.
    const detail = unwrapTemplateResponse(
      await jobChainTemplates.getDetail(saved.id),
    );
    unwrapTemplateResponse(
      await jobChainTemplates.listTemplates({
        scope: saved.scope,
        ...(saved.customerId !== null && { customerId: saved.customerId }),
        jobChainType: saved.jobChainType,
        page: 1,
        pageSize: 100,
      }),
    );
    adopt(detail);
    setUncertain(false);
    setNotice(
      detail.status === 'Active' || detail.status === 'Inactive'
        ? t('jobChainTemplateEditorPublicationConfirmed', {
            version: detail.version,
            status: templateStatusLabel(detail.status, t),
          })
        : t('jobChainTemplateEditorPublicationStillDraft', {
            version: detail.version,
          }),
    );
    await invalidate();
  };
  const focusIssue = (issue: JobChainTemplateIssue) => {
    const target = resolveIssueTarget(graph, issue);
    if (!target) return;
    setActiveTab('workflow');
    setSelection(target.key);
    setTimeout(() => document.getElementById(target.elementId)?.focus(), 0);
  };
  return (
    <DetailCard
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          {metadata.jobChainTypes.find(
            (type) => type.value === saved.jobChainType,
          )?.label || saved.jobChainType}
        </Typography.Title>
      }
      extra={
        <Space wrap>
          <Button onClick={() => navigate(`${templateListPath}${search}`)}>
            {t('jobChainTemplateEditorCancel')}
          </Button>
          {mode === 'view' && saved.status === 'Draft' && can('SaveGraph') && (
            <Button
              type="primary"
              disabled={busy || uncertain}
              onClick={() =>
                navigate(`${templateListPath}/${saved.id}/edit${search}`)
              }
            >
              {t('jobChainTemplateEditorEditDraft')}
            </Button>
          )}
          {can('CreateDraft') && saved.status === 'Active' && (
            <Button
              type="primary"
              disabled={busy || uncertain}
              onClick={() => {
                if (existingDraft) {
                  navigate(
                    `${templateListPath}/${existingDraft.id}/edit${search}`,
                  );
                  return;
                }
                void run(async () => {
                  const result = unwrapTemplateResponse(
                    await jobChainTemplates.createDraft(saved.id),
                  );
                  await cacheDetail(result.template);
                  skipGuard.current = true;
                  navigate(
                    `${templateListPath}/${result.template.id}/edit${search}`,
                  );
                });
              }}
            >
              {existingDraft
                ? t('jobChainTemplateEditorContinueEditing', {
                    version: existingDraft.version,
                  })
                : t('jobChainTemplateEditorCreateDraft')}
            </Button>
          )}
          {editingDraft && can('SaveGraph') && (
            <Button
              disabled={!dirty || busy || uncertain}
              onClick={() =>
                void run(async () => {
                  const result = unwrapTemplateResponse(
                    await jobChainTemplates.saveGraph(
                      saved.id,
                      graph,
                      saved.etag,
                    ),
                  );
                  adopt(result);
                  setNotice(t('jobChainTemplateEditorDraftSaved'));
                  await invalidate();
                })
              }
            >
              {t('jobChainTemplateEditorSaveDraft')}
            </Button>
          )}
          {editingDraft && can('Publish') && (
            <Button
              type="primary"
              disabled={!publishReady}
              onClick={() => setConfirmation('publish')}
            >
              {t('jobChainTemplateEditorPublish')}
            </Button>
          )}
        </Space>
      }
      styles={{ body: { padding: 16 } }}
    >
      <SupportingActions>
        <MetadataSection aria-label={t('jobChainTemplateEditorInformation')}>
          <MetadataItem>
            <dt>
              <GlobalOutlined aria-hidden="true" />{' '}
              {t('jobChainTemplateEditorScope')}
            </dt>
            <dd>{templateScopeLabel(saved.scope, t)}</dd>
          </MetadataItem>
          {saved.customerId !== null && (
            <MetadataItem>
              <dt>
                <BankOutlined aria-hidden="true" />{' '}
                {t('jobChainTemplateEditorCustomer')}
              </dt>
              <dd>
                {metadata.customers.find((item) => item.id === saved.customerId)
                  ?.name || saved.customerId}
              </dd>
            </MetadataItem>
          )}
          <MetadataItem>
            <dt>
              <HistoryOutlined aria-hidden="true" />{' '}
              {t('jobChainTemplateEditorVersion')}
            </dt>
            <dd>
              {t('jobChainTemplateEditorVersionValue', {
                version: saved.version,
              })}
            </dd>
          </MetadataItem>
          <MetadataItem>
            <dt>
              <TagOutlined aria-hidden="true" />{' '}
              {t('jobChainTemplateEditorStatus')}
            </dt>
            <dd>
              <Tag
                color={
                  saved.status === 'Active'
                    ? 'success'
                    : saved.status === 'Draft'
                      ? 'processing'
                      : 'default'
                }
              >
                {templateStatusLabel(saved.status, t)}
              </Tag>
            </dd>
          </MetadataItem>
        </MetadataSection>
        <Space wrap>
          {editingDraft && can('Validate') && (
            <Button
              disabled={dirty || busy || uncertain}
              onClick={() =>
                void run(async () => {
                  clearValidation();
                  const result = unwrapTemplateResponse(
                    await jobChainTemplates.validate(saved.id),
                  );
                  if (result.etag !== saved.etag) {
                    setError(t('jobChainTemplateEditorRevisionChanged'));
                    return;
                  }
                  setValidation(result);
                  setIssues(result.issues);
                  setNotice(
                    result.isValid
                      ? t('jobChainTemplateEditorValidationPassed')
                      : t('jobChainTemplateEditorValidationErrors'),
                  );
                })
              }
            >
              {t('jobChainTemplateEditorValidate')}
            </Button>
          )}
          {editingDraft && can('Discard') && (
            <Button
              danger
              disabled={busy || uncertain}
              onClick={() => setConfirmation('discard')}
            >
              {t('jobChainTemplateEditorDiscardDraft')}
            </Button>
          )}
          <Button disabled={busy} onClick={() => setConfirmation('reload')}>
            {t('jobChainTemplateEditorReload')}
          </Button>
        </Space>
        <span role="status">
          <Tag color={busy ? 'processing' : dirty ? 'warning' : 'default'}>
            {busy
              ? t('jobChainTemplateEditorRequestInProgress')
              : dirty
                ? t('jobChainTemplateEditorUnsavedChanges')
                : t('jobChainTemplateEditorSavedRevision')}
          </Tag>
        </span>
      </SupportingActions>
      <DetailMessages>
        {notice && (
          <Alert role="status" type="info" showIcon message={notice} />
        )}
        {dependencyError ? (
          <Alert
            type="error"
            showIcon
            message={templateFailureText(dependencyError, t)}
            description={t('jobChainTemplateEditorDependencyUnavailable')}
          />
        ) : null}
        {unsupported.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t('jobChainTemplateEditorUnsupportedReadOnly')}
            description={
              <>
                <IssueList>
                  {unsupported.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </IssueList>
                <PreservedGraphFields graph={graph} />
              </>
            }
          />
        )}
        {(error || issues.length > 0) && (
          <ValidationSummary
            ref={summaryRef}
            role="alert"
            tabIndex={-1}
            aria-label={t('jobChainTemplateEditorValidationResultsAria')}
          >
            <Alert
              role="presentation"
              type={
                error || issues.some((issue) => !issue.isWarning)
                  ? 'error'
                  : 'warning'
              }
              showIcon
              message={error || t('jobChainTemplateEditorValidationResults')}
              description={
                issues.length ? (
                  <IssueList>
                    {issues.map((issue, index) => (
                      <li key={index}>
                        <IssueButton
                          type="link"
                          onClick={() => focusIssue(issue)}
                        >
                          {issue.isWarning
                            ? t('jobChainTemplateEditorWarning')
                            : t('jobChainTemplateEditorError')}
                          : {issue.message}
                        </IssueButton>
                      </li>
                    ))}
                  </IssueList>
                ) : undefined
              }
            />
          </ValidationSummary>
        )}
        {uncertain && (
          <Alert
            type="warning"
            showIcon
            message={t('jobChainTemplateEditorPublicationUncertain')}
            description={t('jobChainTemplateEditorPublicationUncertainDetail')}
            action={
              <Button disabled={busy} onClick={() => void run(reconcile)}>
                {t('jobChainTemplateEditorCheckPublication')}
              </Button>
            }
          />
        )}
        {currentValidation && warnings.length > 0 && (
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          >
            {t('jobChainTemplateEditorAcknowledgeWarnings')}
          </Checkbox>
        )}
      </DetailMessages>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyOnHidden={false}
        items={[
          {
            key: 'workflow',
            label: t('jobChainTemplateEditorWorkflow'),
            forceRender: true,
            children: (
              <GraphAuthor
                graph={graph}
                metadata={metadata}
                editable={editable}
                selection={selection}
                onSelect={setSelection}
                onChange={(next: JobChainTemplateGraph) => {
                  if (!editable) return;
                  setGraph(next);
                  clearValidation();
                  setError('');
                  setNotice('');
                  setConfirmation(null);
                }}
              />
            ),
          },
          {
            key: 'history',
            label: t('jobChainTemplateEditorHistoryUsage'),
            children: (
              <TabSection aria-label={t('jobChainTemplateEditorHistoryUsage')}>
                <Descriptions
                  size="small"
                  column={{ xs: 1, sm: 2 }}
                  items={[
                    {
                      key: 'total',
                      label: t('jobChainTemplateEditorTotalJobChains'),
                      children: saved.totalUsage,
                    },
                    {
                      key: 'running',
                      label: t('jobChainTemplateEditorRunningJobChains'),
                      children: saved.runningUsage,
                    },
                    ...(saved.previousPublishedTemplateId !== null
                      ? [
                          {
                            key: 'previous',
                            label: t('jobChainTemplateEditorPreviousVersion'),
                            children: (
                              <Link
                                to={`${templateListPath}/${saved.previousPublishedTemplateId}${search}`}
                              >
                                {t('jobChainTemplateEditorViewPrevious')}
                              </Link>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                {saved.publication ? (
                  <Descriptions
                    size="small"
                    column={{ xs: 1, sm: 2, lg: 3 }}
                    items={[
                      {
                        key: 'actor',
                        label: t('jobChainTemplateEditorPublishedBy'),
                        children: saved.publication.actor,
                      },
                      {
                        key: 'time',
                        label: t('jobChainTemplateEditorPublishedAt'),
                        children: (
                          <time dateTime={saved.publication.timeUtc}>
                            {saved.publication.timeUtc}
                          </time>
                        ),
                      },
                      {
                        key: 'result',
                        label: t('jobChainTemplateEditorResult'),
                        children: saved.publication.result,
                      },
                    ]}
                  />
                ) : (
                  <Typography.Text type="secondary">
                    {t('jobChainTemplateEditorNoPublication')}
                  </Typography.Text>
                )}
                <List
                  size="small"
                  header={
                    <Typography.Text strong>
                      {t('jobChainTemplateEditorVersionHistory')}
                    </Typography.Text>
                  }
                  dataSource={[...saved.history].sort(
                    (a, b) => b.version - a.version,
                  )}
                  locale={{
                    emptyText: t('jobChainTemplateEditorNoVersionHistory'),
                  }}
                  renderItem={(item) => (
                    <List.Item key={item.id}>
                      <Link to={`${templateListPath}/${item.id}${search}`}>
                        {t('jobChainTemplateEditorVersionStatus', {
                          version: item.version,
                          status: templateStatusLabel(item.status, t),
                        })}
                      </Link>
                    </List.Item>
                  )}
                />
              </TabSection>
            ),
          },
          {
            key: 'audit',
            label: t('jobChainTemplateEditorAudit'),
            children: (
              <TabSection aria-label={t('jobChainTemplateEditorAudit')}>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={saved.audit}
                  locale={{
                    emptyText: t('jobChainTemplateEditorNoAuditEvents'),
                  }}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: t('jobChainTemplateEditorAction'),
                      dataIndex: 'action',
                      key: 'action',
                      render: (action: string) => auditActionText(action, t),
                    },
                    {
                      title: t('jobChainTemplateEditorActor'),
                      dataIndex: 'actor',
                      key: 'actor',
                    },
                    {
                      title: t('jobChainTemplateEditorTimeUtc'),
                      dataIndex: 'timeUtc',
                      key: 'timeUtc',
                      render: (timeUtc: string) => (
                        <time dateTime={timeUtc}>{timeUtc}</time>
                      ),
                    },
                    {
                      title: t('jobChainTemplateEditorResult'),
                      dataIndex: 'result',
                      key: 'result',
                    },
                    {
                      title: t('jobChainTemplateEditorReferenceColumn'),
                      dataIndex: 'correlationId',
                      key: 'correlationId',
                      render: (correlationId: string | null) =>
                        correlationId || t('jobChainTemplatesNotAvailable'),
                    },
                  ]}
                />
              </TabSection>
            ),
          },
        ]}
      />
      {blocker.state === 'blocked' && (
        <ConfirmDialog
          title={t('jobChainTemplateEditorLeaveTitle')}
          confirm={t('jobChainTemplateEditorLeaveConfirm')}
          onCancel={() => blocker.reset()}
          onConfirm={() => blocker.proceed()}
        >
          <p>{t('jobChainTemplateEditorLeaveDetail')}</p>
          <Button onClick={() => blocker.reset()}>
            {t('jobChainTemplateEditorStay')}
          </Button>
        </ConfirmDialog>
      )}
      {confirmation === 'reload' && (
        <ConfirmDialog
          title={t('jobChainTemplateEditorReloadTitle')}
          confirm={t('jobChainTemplateEditorReloadConfirm')}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            setConfirmation(null);
            void run(async () => {
              if (uncertain) await reconcile();
              else {
                const [detail, refreshedMetadata] = await Promise.all([
                  jobChainTemplates.getDetail(saved.id),
                  jobChainTemplates.getMetadata(),
                ]);
                unwrapTemplateResponse(refreshedMetadata);
                adopt(unwrapTemplateResponse(detail));
                await invalidate();
              }
            });
          }}
        >
          <p>{t('jobChainTemplateEditorReloadDetail')}</p>
        </ConfirmDialog>
      )}
      {confirmation === 'discard' && (
        <ConfirmDialog
          title={t('jobChainTemplateEditorDiscardTitle')}
          confirm={t('jobChainTemplateEditorDiscardConfirm')}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            setConfirmation(null);
            void run(async () => {
              unwrapTemplateResponse(
                await jobChainTemplates.discard(saved.id, saved.etag),
              );
              skipGuard.current = true;
              navigate(`${templateListPath}${search}`);
              await invalidate();
            });
          }}
        >
          <p>{t('jobChainTemplateEditorDiscardDetail')}</p>
        </ConfirmDialog>
      )}
      {confirmation === 'publish' && (
        <ConfirmDialog
          title={t('jobChainTemplateEditorPublishTitle', {
            version: saved.version,
          })}
          confirm={t('jobChainTemplateEditorPublishConfirm')}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            setConfirmation(null);
            if (!publishReady) return;
            void run(async () => {
              try {
                const result = unwrapTemplateResponse(
                  await jobChainTemplates.publish(
                    saved.id,
                    {
                      acknowledgedWarnings: acknowledged
                        ? [...new Set(warnings.map((issue) => issue.code))]
                        : [],
                    },
                    saved.etag,
                  ),
                );
                adopt(result);
                setNotice(
                  t('jobChainTemplateEditorPublishedNotice', {
                    version: result.version,
                  }),
                );
                await invalidate();
              } catch (failure) {
                clearValidation();
                if (
                  !isJobChainTemplateApiFailure(failure) ||
                  failure.code >= 500 ||
                  failure.code === 0
                ) {
                  setUncertain(true);
                  fail(failure);
                  await reconcile();
                } else throw failure;
              }
            });
          }}
        >
          <p>
            {t('jobChainTemplateEditorPublishDetail', {
              version: saved.version,
            })}
          </p>
        </ConfirmDialog>
      )}
    </DetailCard>
  );
};
